/**
 * 견적서 상세 컴포넌트
 * 계약의 견적서 상세 정보를 표시하는 컴포넌트입니다.
 * 견적서 ID, 총액, 이용 시간 등의 기본 정보와 함께 공용공간별 세부 견적 정보를 테이블 형태로 보여줍니다.
 */

import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import BaseScroll from "src/components/BaseScroll";
import Link from "src/components/PartnerLink";
import { PagePath } from "src/pages/product/details";
import { numberToStringWithComma, YmdFormat } from 'src/utils';
import BaseSection from "../../../../components/layout/BaseSection";
import useContractApi from "../../hooks/useContractApi";
import useEstimateApi from "../../hooks/useEstimateApi";
import useProductFacilityApi from "../../hooks/useProductFacilityApi";
import { BaseDatePicker, BaseSelect, BaseTooltip } from "src/components";
import { QuotationItem, QuotationItemListModel, QuotationModel } from "src/api/contract/contract-types";

/**
 * 컴포넌트 props 인터페이스
 */
interface BaseProps {
  detailData: any; // 계약 상세 데이터
  viewOnly?: boolean; // 읽기 전용 모드
  onChangeQuotationItem?: (payload: QuotationItemListModel | null) => void;
}

/**
 * 견적서 상세 컴포넌트
 * @param detailData 계약 상세 데이터
 * @param viewOnly 읽기 전용 모드
 */
const EstimateEdit = ({ detailData, viewOnly, onChangeQuotationItem }: BaseProps) => {
  const { partnerId } = useParams();
  const { getProductData } = useContractApi();
  const { getEstimateList } = useEstimateApi();
  const { makeSpaceList } = useProductFacilityApi();

  const [estimateData, SetEstimateData] = useState<QuotationModel[]>([]); // 견적서 데이터 상태
  const [lastEstimateData, setLastEstimateData] = useState<QuotationItem>(); // 최신 견적서
  // 회의실 셀렉트 옵션
  const [meetingRoomOptions, setMeetingRoomOptions] = useState<{ value: string; label: string }[]>([]);

  const [selectedFacility, setSelectedFacility] = useState<string>("");
  // 이용 시작/종료 시간 state
  const [useStartTime, setUseStartTime] = useState<Date | null>(null);
  const [useEndTime, setUseEndTime] = useState<Date | null>(null);
  const [useTimeError, setUseTimeError] = useState<string>("");

  const emitQuotationItem = (startTime: Date | null = useStartTime, endTime: Date | null = useEndTime) => {
    if (!onChangeQuotationItem) return;

    // 필수값이 없거나 시간 유효성 에러가 있으면 null 전달
    if (!selectedFacility || !startTime || !endTime) {
      onChangeQuotationItem(null);
      return;
    }

    // 종료 시간이 시작 시간보다 이전이거나 같으면 null 전달
    if (endTime <= startTime) {
      onChangeQuotationItem(null);
      return;
    }

    const payload: QuotationItemListModel = {
      productId: String(detailData?.product?.id ?? ""),
      buildingCommonFacilityId: selectedFacility,
      startDate: moment(startTime).format(YmdFormat.WITH_TIME_ZONE),
      endDate: moment(endTime).format(YmdFormat.WITH_TIME_ZONE),
    };

    onChangeQuotationItem(payload);
  };
  /**
   * 견적서 데이터 초기화 함수
   * 견적서 목록을 조회하고 공용공간 정보와 매핑하여 데이터를 구성합니다.
   */
  const initDatas = useCallback(async () => {
    if (detailData?.contract?.quotationList) {
      let ids = "";
      detailData?.contract?.quotationList.forEach((list: any) => {
        if (ids !== "") {
          ids = ids + "," + list.quotationId;
        } else {
          ids = list.quotationId;
        }
      });

      let tableData: any = [];
      const result = await getProductData(Number(detailData?.contractManage?.spaceProductId));
      if (result) {
        tableData = await makeSpaceList(result);

        // buildingCommonFacility.meetingRoomList 기반 회의실 옵션 생성
        const meetingOptions: { value: string; label: string }[] = [];
        result?.buildingList?.forEach((building: any) => {
          const buildingName = building.buildingName;
          building.buildingCommonFacility?.meetingRoomList?.forEach((room: any) => {
            meetingOptions.push({
              value: String(room.id),
              label: `${buildingName} - ${room.facilityName ?? room.name ?? `회의실 ${room.id}`}`,
            });
          });
        });
        setMeetingRoomOptions(meetingOptions);
      }
      const response = await getEstimateList({ id: ids });
      if (response.data.data.content) {
        response.data.data.content.forEach((item: any) => {
          const find: any = tableData.find((list: any) => list.id === item?.quotationItemList[0].buildingCommonFacilityId);

          if (find) {
            item.buildingCommonFacilityId = item?.quotationItemList[0].buildingCommonFacilityId;
            item.buildingCommonFacilityName = find.facilityName;
            item.commonFacilityType =
              find.commonFacilityType === "MEETING_ROOM" ? "회의실" : find.commonFacilityType === "REFRESH_ROOM" ? "휴게공간" : "좌석";
            item.facilityMaxPeopleNums = find.facilityMaxPeopleNums;
            item.isGround = find.isGround;
            item.floorNum = find.floorNum;
            setSelectedFacility(String(item.buildingCommonFacilityId));
          } else {
            item.buildingCommonFacilityId = 0;
            item.buildingCommonFacilityName = "없는 공용공간입니다.";
            item.commonFacilityType = "유형이 없습니다.";
            item.facilityMaxPeopleNums = 0;
            item.isGround = false;
            item.floorNum = 0;
          }
          item.subRows = item?.quotationItemList;
        });
        SetEstimateData(response.data.data.content);
        const lastEstimate = response.data.data.content?.slice(-1)?.[0]?.quotationItemList?.[0]
        console.log('lastEstimate', lastEstimate);
        if (lastEstimate) {
          setLastEstimateData(lastEstimate as QuotationItem)
        }
      }
    }
  }, [detailData?.contract?.quotationList, detailData?.contractManage?.spaceProductId, getEstimateList, getProductData, makeSpaceList]);

  /**
   * 공용공간 상세 페이지로 이동하는 URL 생성 함수
   * @param productId 상품 ID
   * @returns 공용공간 상세 페이지 URL
   */
  const moveProductFacility = (productId: string) => {
    return PagePath.product.detail.replace(":partenrId", String(partnerId)).replace(":id", productId) + "?tab=commonSpace";
  };

  // 컴포넌트 마운트 시 견적서 데이터 초기화
  useEffect(() => {
    initDatas();
  }, [initDatas]);

  // 이용 시작/종료 시간 초기값 및 검증
  useEffect(() => {
    const start = lastEstimateData?.startDate ? moment(lastEstimateData?.startDate).toDate() : null;
    const end = lastEstimateData?.endDate ? moment(lastEstimateData?.endDate).toDate() : null;
    setUseStartTime(start);
    setUseEndTime(end);

    if (start && end && end <= start) {
      setUseTimeError("이용 종료 시간은 이용 시작 시간보다 이후여야 합니다.");
    } else {
      setUseTimeError("");
    }
  }, [lastEstimateData?.startDate, lastEstimateData?.endDate]);

  // selectedFacility / useStartTime / useEndTime 이 모두 준비되면 payload를 상위로 올려줌
  useEffect(() => {
    emitQuotationItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacility, useStartTime, useEndTime]);


  return (
    <BaseScroll maxHeight={600}>
      {estimateData.slice(-1).map((item: any, index: number) => {
        return (
          <div key={index} className={index === 0 ? "" : "mt20"}>
            <div>
              <div className="flex-row flex-center">
                <BaseSection title={"id(견적서 고유ID)"} value={item.id + " (" + item.quotationNo + ")"} />

                {/* <BaseSection title={"견적서 총액(부가세 별도)"} value={numberToStringWithComma(item.amount) + " 원"} /> */}
              </div>
              <div className="flex-row flex-center">
                <BaseSection
                  title={"공용공간"}
                  required={true}
                  children={
                    <BaseSelect
                      menuPlacement="auto"
                      className="minmax240"
                      stateOptions={meetingRoomOptions}
                      value={selectedFacility}
                      placeholder="선택해주세요"
                      setStateValue={(value: string) => {
                        setSelectedFacility(value);
                        emitQuotationItem();
                      }}
                    />
                  }
                />
              </div>
              <div className="flex-row flex-center">
                <BaseSection
                  title={"이용 시작 시간"}
                  required={true}
                  children={
                    <BaseDatePicker
                      type="full"
                      name={"useStartTime"}
                      className="minmax240"
                      timeIntervals={30}
                      selectedDate={useStartTime}
                      setDate={(date: string) => {
                        if (date) {
                          const newStart = moment(date).toDate();
                          setUseStartTime(newStart);

                          // 종료 시간과 비교하여 검증
                          if (useEndTime && useEndTime <= newStart) {
                            setUseTimeError("이용 종료 시간은 이용 시작 시간보다 이후여야 합니다.");
                          } else {
                            setUseTimeError("");
                          }
                          emitQuotationItem(newStart, useEndTime);
                        } else {
                          setUseStartTime(null);
                          setUseTimeError("");
                          emitQuotationItem(null, useEndTime);
                        }
                      }}
                    />
                  }
                />
                <BaseSection
                  title={"이용 종료 시간"}
                  required={true}
                  children={
                    <BaseDatePicker
                      type="full"
                      className="minmax240"
                      name={"useEndTime"}
                      timeIntervals={30}
                      selectedDate={useEndTime}
                      setDate={(date: string) => {
                        if (date) {
                          const newEnd = moment(date).toDate();
                          setUseEndTime(newEnd);

                          // 시작 시간과 비교하여 검증
                          if (useStartTime && newEnd <= useStartTime) {
                            setUseTimeError("이용 종료 시간은 이용 시작 시간보다 이후여야 합니다.");
                          } else {
                            setUseTimeError("");
                          }
                          emitQuotationItem(useStartTime, newEnd);
                        } else {
                          setUseEndTime(null);
                          setUseTimeError("");
                          emitQuotationItem(useStartTime, null);
                        }
                      }}
                    />
                  }
                />
              </div>
              {useTimeError && <BaseSection
                title={" "}
                children={<p className="validation-text pa0">{useTimeError}</p>}
              />}

            </div>
            <BaseSection
              title={"세부 견적서"}
              children={
                <div className="flex-row d-flex flex-start w-100">
                  <section className="contents-container__grid">
                    <div className="flex-row flex-start">
                      <div className="base-table">
                        <div className="header">
                          <div className="flex-row flex-start base-table__tr">
                            <div className="base-table__th minmax200">공용공간명</div>
                            <div className="base-table__th minmax100">유형</div>
                            <div className="base-table__th minmax160">층</div>
                            <div className="base-table__th minmax160">정원</div>
                            <div className="base-table__th minmax220">금액(부가세 별도)</div>
                          </div>
                        </div>
                        <div className="body">
                          {item.subRows.map((row: any, idx: number) => {
                            return (
                              <div className={`base-table__tr flex-row flex-start `} key={idx}>
                                <div className="base-table__td minmax200 text-left">
                                  {item.buildingCommonFacilityId !== 0 ? (
                                    <Link to={moveProductFacility(detailData?.product?.id)} className="text-underline" target={"_blank"}>
                                      <p>
                                        {item.buildingCommonFacilityName.length > 15
                                          ? item.buildingCommonFacilityName.substring(0, 12) + "..."
                                          : item.buildingCommonFacilityName}
                                      </p>
                                      {item.buildingCommonFacilityName.length > 15 && (
                                        <BaseTooltip contents={""} tooltip={item.buildingCommonFacilityName} touchIcon={"QUESTION"} />
                                      )}
                                    </Link>
                                  ) : (
                                    <div>
                                      <p>
                                        {item.buildingCommonFacilityName.length > 15
                                          ? item.buildingCommonFacilityName.substring(0, 12) + "..."
                                          : item.buildingCommonFacilityName}
                                      </p>
                                      {item.buildingCommonFacilityName.length > 15 && (
                                        <BaseTooltip contents={""} tooltip={item.buildingCommonFacilityName} touchIcon={"QUESTION"} />
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="base-table__td minmax100">
                                  <p>{item.commonFacilityType}</p>
                                </div>
                                <div className="base-table__td minmax160">
                                  <p>{item.isGround ? "지상 " + item.floorNum + "층" : "지하 " + item.floorNum + "층"}</p>
                                </div>
                                <div className="base-table__td minmax160">
                                  <p>{item.facilityMaxPeopleNums}명</p>
                                </div>
                                <div className="base-table__td minmax220">
                                  <p>{numberToStringWithComma(row.price)} 원</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              }
            />
          </div>
        );
      })}
    </BaseScroll>
  );
};
export default EstimateEdit;
